export type IamSlideupAnimation = 'top' | 'bottom';

export interface IamSlideupThemeColors {
    background: string;
    textColor: string;
}

export interface IamSlideupConfig {
    title: string;
    subtitle: string;
    closeButton: boolean;
    promoImage: string | null;
    webUrl: string | null;
    animation: IamSlideupAnimation;
    colors: {
        light: IamSlideupThemeColors;
        dark: IamSlideupThemeColors;
    };
}

export const IAM_SLIDEUP_DEFAULTS = {
    closeButton: true,
    animation: 'bottom' as IamSlideupAnimation,
    cornerRadiusPx: 12,
    colors: {
        light: { background: '#B75EFF', textColor: '#FFFFFF' },
        dark: { background: '#8A64FA', textColor: '#FFFFFF' },
    },
} as const;

const HEX_COLOR = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

const asString = (value: unknown): string | undefined => (typeof value === 'string' ? value : undefined);
const asHexColor = (value: unknown, fallback: string): string => (typeof value === 'string' && HEX_COLOR.test(value) ? value : fallback);

const asHttpUrl = (value: unknown): string | null => {
    const raw = asString(value)?.trim();
    if (!raw) {
        return null;
    }
    try {
        const url = new URL(raw);
        return url.protocol === 'https:' || url.protocol === 'http:' ? raw : null;
    } catch {
        return null;
    }
};

type IamBackgroundType = 'solid' | 'gradient';

const buildThemeColors = (input: unknown, backgroundType: IamBackgroundType, fallback: IamSlideupThemeColors): IamSlideupThemeColors => {
    const source = input && typeof input === 'object' ? (input as Record<string, unknown>) : {};
    const textColor = asHexColor(source.text_color, fallback.textColor);

    if (backgroundType === 'gradient') {
        const topColor = asHexColor(source.background_top_color, fallback.background);
        const bottomColor = asHexColor(source.background_bottom_color, fallback.background);
        return { background: `linear-gradient(180deg, ${topColor} 0%, ${bottomColor} 100%)`, textColor };
    }
    return { background: asHexColor(source.background_color, fallback.background), textColor };
};

// Parse the `json` extra string into a validated config. Returns null when absent, invalid, or
// empty (no title/subtitle) — the caller then falls back to the native Braze message.
export const parseIamMessageData = (raw?: string | null): IamSlideupConfig | null => {
    if (!raw || typeof raw !== 'string') {
        return null;
    }

    let data: Record<string, unknown>;
    try {
        const parsed: unknown = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') {
            return null;
        }
        data = parsed as Record<string, unknown>;
    } catch {
        return null;
    }

    const title = asString(data.title)?.trim() ?? '';
    const subtitle = asString(data.subtitle)?.trim() ?? '';
    if (!title && !subtitle) {
        return null;
    }

    const colors = data.colors && typeof data.colors === 'object' ? (data.colors as Record<string, unknown>) : {};
    const backgroundType: IamBackgroundType = data.background_type === 'gradient' ? 'gradient' : 'solid';

    return {
        title,
        subtitle,
        closeButton: typeof data.close_button === 'boolean' ? data.close_button : IAM_SLIDEUP_DEFAULTS.closeButton,
        promoImage: asHttpUrl(data.promo_image),
        // Web uses web_url only; deeplink (mobile) is intentionally ignored.
        webUrl: asString(data.web_url)?.trim() || null,
        animation: data.animation === 'top' ? 'top' : IAM_SLIDEUP_DEFAULTS.animation,
        colors: {
            light: buildThemeColors(colors.light_theme, backgroundType, IAM_SLIDEUP_DEFAULTS.colors.light),
            dark: buildThemeColors(colors.dark_theme, backgroundType, IAM_SLIDEUP_DEFAULTS.colors.dark),
        },
    };
};

export interface IamWebUrlResolution {
    kind: 'route' | 'external' | 'none';
    target?: string;
}

// Resolve the slide-up `web_url` into a web action: internal SPA route, external URL, or nothing
// (nothing → the widget just closes on click).
export const resolveIamWebUrl = (webUrl: string | null): IamWebUrlResolution => {
    if (!webUrl) {
        return { kind: 'none' };
    }

    const base = typeof window !== 'undefined' ? window.location.origin : undefined;
    let url: URL;
    try {
        url = new URL(webUrl, base);
    } catch {
        return { kind: 'none' };
    }

    if (url.protocol === 'http:' || url.protocol === 'https:') {
        if (typeof window !== 'undefined' && url.origin === window.location.origin) {
            return { kind: 'route', target: `${url.pathname}${url.search}${url.hash}` };
        }
        return { kind: 'external', target: url.href };
    }

    return { kind: 'none' };
};
